"use client";
import { CompositeItem, useCompositeItem } from "../composite/composite-item.js";
import { useTabScopedContext } from "./tab-context.js";
import { createElement, createHook, forwardRef, memo, useEvent, useId } from "@ariakit/react-utils";
import { disabledFromProps, invariant } from "@ariakit/utils";
import { useCallback } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/tab/tab.tsx
const TagName = "button";
/**
* Returns props to create a `Tab` component.
* @see https://ariakit.com/components/tab
* @example
* ```jsx
* const store = useTabStore();
* const props = useTab({ store });
* <TabList store={store}>
*   <Role {...props}>Tab 1</Role>
* </TabList>
* <TabPanel store={store}>Panel 1</TabPanel>
* ```
*/
const useTab = createHook(function useTab({ store, getItem: getItemProp, ...props }) {
	const context = useTabScopedContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "Tab must be wrapped in a TabList component.");
	const defaultId = useId();
	const id = props.id || defaultId;
	const dimmed = disabledFromProps(props);
	const getItem = useCallback((item) => {
		const nextItem = {
			...item,
			dimmed
		};
		if (getItemProp) return getItemProp(nextItem);
		return nextItem;
	}, [dimmed, getItemProp]);
	const onClickProp = props.onClick;
	const onClick = useEvent((event) => {
		onClickProp?.(event);
		if (event.defaultPrevented) return;
		store?.setSelectedId(id);
	});
	const panelId = useStoreState(store.panels, () => store.panel(id)?.id);
	const shouldRegisterItem = defaultId ? props.shouldRegisterItem : false;
	const isActive = useStoreState(store, (state) => !!id && state.activeId === id);
	const selected = useStoreState(store, (state) => !!id && state.selectedId === id);
	const hasActiveItem = useStoreState(store, (state) => !!store.item(state.activeId));
	const canRegisterComposedItem = isActive || selected && !hasActiveItem;
	const accessibleWhenDisabled = selected || (props.accessibleWhenDisabled ?? true);
	if (useStoreState(store.combobox || store.composite, "virtualFocus")) props = {
		...props,
		tabIndex: -1
	};
	props = {
		role: "tab",
		"aria-selected": selected,
		"aria-controls": panelId || void 0,
		...props,
		id,
		onClick
	};
	if (store.composite) {
		const defaultProps = {
			id,
			accessibleWhenDisabled,
			store: store.composite,
			shouldRegisterItem: canRegisterComposedItem && shouldRegisterItem,
			rowId: props.rowId,
			render: props.render
		};
		props = {
			...props,
			render: /* @__PURE__ */ jsx(CompositeItem, {
				...defaultProps,
				render: store.combobox && store.composite !== store.combobox ? /* @__PURE__ */ jsx(CompositeItem, {
					...defaultProps,
					store: store.combobox
				}) : defaultProps.render
			})
		};
	}
	props = useCompositeItem({
		store,
		...props,
		accessibleWhenDisabled,
		getItem,
		shouldRegisterItem
	});
	return props;
});
/**
* Renders a tab element inside a
* [`TabList`](https://ariakit.com/reference/tab-list) wrapper.
* @see https://ariakit.com/components/tab
* @example
* ```jsx {3,4}
* <TabProvider>
*   <TabList>
*     <Tab>Tab 1</Tab>
*     <Tab>Tab 2</Tab>
*   </TabList>
*   <TabPanel>Panel 1</TabPanel>
*   <TabPanel>Panel 2</TabPanel>
* </TabProvider>
* ```
*/
const Tab = memo(forwardRef(function Tab(props) {
	return createElement(TagName, useTab(props));
}));
//#endregion
export { Tab, useTab };

//# sourceMappingURL=tab.js.map